<template>
  <div
    class="rechercheDetailMainWrapper"
    ref="rechercheDetailMainWrapper"
  >
    <div class="navigationWrapper">
      <BasicButton
        type="reverse"
        iconName="arrow-left"
        label="Retour à la liste"
        @click="useDossierRoutingService().navigateToRechercheDossierList"
      />
    </div>
    <div class="topWrapper">
      <BasicCard
        class="cardDesktop"
        :title="`${DispositifLibelle[store.model!.dispositif!]}` + ' ' + 'N°' + `${store.model?.code}`"
        :subtitles="informationDossierSubtitles"
        :badge="{ label: StatutLibelle[itemStatut], type: StatutBadgeType[itemStatut] }"
      />
      <BasicCard
        class="cardMobile"
        :title="`${DispositifLibelle[store.model!.dispositif!]}` + ' ' + 'N°' + `${store.model?.code}`"
        :subtitles="informationDossierSubtitles"
        :badge="{ label: StatutLibelle[itemStatut], type: StatutBadgeType[itemStatut] }"
      />
    </div>
    <div class="contentWrapper">
      <div class="tripleSection">
        <div class="sectionWrapper">
          <span class="sectionTitle">Modules ({{ store.model?.modules?.length }})</span>
          <div class="detailWrapper">
            <BasicCard
              v-for="(module, index) in modulesList"
              :key="index"
              :title="module.intitule"
              :subtitles="[
                { text: module.etablissementPrestataire.denomination! },
                { text: dureeDeLaFormation(module), icon: 'clock' },
                { text: datesDeLaFormation(module), icon: 'calendar' },
              ]"
            />
            <BasicButton
              v-if="store.model?.modules?.length! > 1"
              @click="
                ((showFullModulesList = !showFullModulesList),
                (showFullStagiairesList = false),
                (showFinancementDetail = false))
              "
              variant="outlined"
              :label="!showFullModulesList ? 'Déployer la liste' : 'Réduire la liste'"
              iconRight
              :iconName="!showFullModulesList ? 'arrow-down' : 'arrow-up'"
            />
          </div>
        </div>
        <div class="sectionWrapper">
          <div class="sectionHeading">
            <span class="sectionTitle">Stagiaires ({{ store.model?.stagiaires?.length || 0 }})</span>
          </div>
          <div
            class="stagiaireSectionBlock"
            v-for="(listeDeStagiaires, index) in stagiairesParEtablissement"
            :key="index"
          >
            <div class="sectionHeading">
              <span
                class="sectionSubtitle"
              >
                {{ stagiairesParEtablissement![index][0].etablissementAdherent?.denomination }}
                {{ stagiairesParEtablissement![index][0].etablissementAdherent?.siret }}
              </span>
            </div>
            <div class="detailWrapper">
              <StagiairesList
                :listeStagiaires="listeDeStagiaires"
                :nombreDeListes="stagiairesParEtablissement?.length || 0"
                />
            </div>
          </div>
        </div>
        <div class="sectionWrapper sectionWrapper--financements">
          <div class="financementsTitleWrapper">
            <div class="sectionHeading">
              <span class="sectionTitle">Financements ({{ store.model?.financements!.length }})</span>
              <span class="sectionSubtitle">
                {{ store.model?.avecSubrogation ? 'avec subrogation' : 'sans subrogation' }}
              </span>
            </div>
            <BasicButton
              v-if="store.model?.financements!.length"
              class="buttonDesktop"
              :label="!showFinancementDetail ? 'Voir le détail' : 'Masquer le détail'"
              :iconName="!showFinancementDetail ? 'eye' : 'eye-slash'"
              iconRight
              @click="
                ((showFinancementDetail = !showFinancementDetail),
                (showFullModulesList = false),
                (showFullStagiairesList = false))
              "
            />
          </div>
          <div class="financementsGrid">
            <div class="financementsGridElem">
              <span class="financementsTitle">Demandé *</span>
              <span class="financementsSubtitle">{{ financementDemandeRecap }} €</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Accordé</span>
              <span class="financementsSubtitle">{{ financementAccordeRecap }} €</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Réglé</span>
              <span class="financementsSubtitle">{{ financementRegleRecap }} €</span>
            </div>
            <div class="financementsGridElem">
              <span class="financementsTitle">Solde</span>
              <span class="financementsSubtitle">{{ financementSoldeRecap }} €</span>
            </div>
          </div>
          <BasicText
            size="body-m"
            color="white"
          >
            * {{ asterisqueLabel }}
          </BasicText>
          <BasicButton
            v-if="store.model?.financements![0]"
            class="buttonMobile"
            :label="!showFinancementDetail ? 'Voir le détail' : 'Masquer le détail'"
            :iconName="!showFinancementDetail ? 'arrow-down' : 'arrow-up'"
            type="secondary"
            iconRight
            @click="((showFinancementDetail = !showFinancementDetail), (showFullStagiairesList = false))"
          />
        </div>
      </div>
    </div>
    <Transition>
      <FinancementDetail
        v-if="showFinancementDetail"
        :detailModulesList
      />
    </Transition>
    <div class="documentsMainWrapper">
      <DocumentsList
        :code="store.model?.code!"
        :dispositif="store.model!.dispositif!"
        endpoint="dossiers"
        :pieces="store.model?.pieces!"
        :statut="store.model?.statut!"
      />
      <CourriersList
        :courriers="store.model?.courriers!"
        :code="store.model?.code!"
        endpoint="dossiers"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { DossierDTO, StagiaireDTO } from '@/features/api/extranet/dto/Dossier/Synthese'
  import type { ModuleDTO } from '@/features/api/extranet/dto/Dossier/Synthese/ModuleDTO'
  import { DispositifLibelle } from '@/features/domaine/types/opco'
  import { StatutBadgeType, StatutLibelle } from '@/features/domaine/types/opco/Dossier'
  import { dateTimeToReadableDate, isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'
  import { dateFormatter } from '@/features/shared/tools/date'
  import { getLovIntitule } from '../../shared/tools'
  import CourriersList from '../components/CourriersList.vue'
  import DocumentsList from '../components/DocumentsList.vue'
  import FinancementDetail from './components/FinancementDetail.vue'
  import StagiairesList from './components/StagiairesList.vue'
  import { useDossierDetailHelper } from './useDossierDetailHelper'
  import { useDossierRoutingService } from './useDossierRoutingService'
  import { useDossierStore } from './useDossierStore'

  const store = useDossierStore()
  const showFinancementDetail = ref(false)
  const showFullModulesList = ref(false)
  const showFullStagiairesList = ref(false)
  const {
    itemStatut,
    financementDemandeRecap,
    financementAccordeRecap,
    financementRegleRecap,
    financementSoldeRecap,
    detailModulesList,
  } = useDossierDetailHelper(store.model as DossierDTO)

  const dureeDeLaFormation = (module: ModuleDTO) => {
    let dureeString: string = ''
    if (module.duree?.heure) dureeString = dureeString + module.duree?.heure.toString() + 'h'
    if (module.duree?.minute) dureeString = dureeString + module.duree?.minute.toString() + 'min'
    return isNotNullUndefinedOrEmptyString(dureeString) ? dureeString : '-'
  }

  const datesDeLaFormation = (module: ModuleDTO) => {
    const dateDebut = module.dateDebut ? dateFormatter(module.dateDebut) : '-'
    const dateFin = module.dateFin ? dateFormatter(module.dateFin) : '-'
    return `Du ${dateDebut} au ${dateFin}`
  }

  const modulesList = computed(() => {
    if ((store.model?.modules?.length ?? 0) > 1 && !showFullModulesList.value) {
      return store.model?.modules?.slice(0, 1)
    }
    return store.model?.modules
  })

  const stagiairesParEtablissement = computed(() => {
    if (store.model?.stagiaires?.length) {
      ;[...store.model.stagiaires].sort((a, b) =>
        a.etablissementAdherent!.siret!.localeCompare(b.etablissementAdherent!.siret!),
      )
      const stagiairesParEtablissement = {} as any

      store.model?.stagiaires.forEach((stagiaire) => {
        const siret = stagiaire.etablissementAdherent!.siret!
        if (!stagiairesParEtablissement[siret]) stagiairesParEtablissement[siret] = []
        stagiairesParEtablissement[siret].push(stagiaire)
      })
      return Object.values(stagiairesParEtablissement) as StagiaireDTO[][]
    }
    return undefined
  })

  const asterisqueLabel = computed(() => {
    return store.model?.dispositif === 'CAPP'
      ? 'N’inclut pas l’EFT (Exercice à la fonction tutorale) exprimé en mois.'
      : store.model?.dispositif === 'CPRO'
        ? 'N’inclut pas l’EFT (Exercice à la fonction tutorale) exprimé en mois et le coût pédagogique exprimé en heures.'
        : 'N’inclus que les coûts demandés exprimés en monétaire.'
  })

  const informationDossierSubtitles = computed(() => {
    const subtitles: SubtitleProps[] = []
    subtitles.push({
      text: store.model?.sousDispositif ? getLovIntitule(store.lov!.SousDispositif, store.model?.sousDispositif) : '-',
    })
    if (store.model?.dispositif === 'CPRO' || store.model?.dispositif === 'CAPP')
      subtitles.push({ text: 'Numéro de contrat DGEFP : ' + `${store.model?.numeroDeca ?? '-'}` })
    subtitles.push({ text: 'Créé le ' + `${dateTimeToReadableDate(store.model?.dateReception)}` })

    return subtitles
  })
</script>

<style scoped lang="less">
  @import '../index.less';
</style>

